import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL-backed performance date range. Picks one of a fixed set of windows
 * (7 / 30 / 90 days) via `?range=` and resolves to ISO `from` / `to` for the
 * insights API plus a `lookback` for the previous-period comparison. Hooks
 * that fetch insights take the resolved values directly so the SWR cache key
 * naturally invalidates when the window changes.
 *
 * The label is what the design shows on the "Showing data for" pill — kept
 * here so the page and the picker don't drift apart.
 */
export const DATE_RANGE_OPTIONS = [
	{ value: '7d', label: 'Last 7 days', days: 7 },
	{ value: '30d', label: 'Last 30 days', days: 30 },
	{ value: '90d', label: 'Last 90 days', days: 90 }
];

const DEFAULT_RANGE = '30d';

const iso = (d) => d.toISOString().slice(0, 10);

export function useDateRange() {
	const [searchParams, setSearchParams] = useSearchParams();
	const value = searchParams.get('range') ?? DEFAULT_RANGE;
	const option = DATE_RANGE_OPTIONS.find((o) => o.value === value) ?? DATE_RANGE_OPTIONS.find((o) => o.value === DEFAULT_RANGE);

	const range = useMemo(() => {
		const to = new Date();
		const from = new Date(to.getTime() - option.days * 24 * 60 * 60 * 1000);
		const lookbackTo = new Date(from.getTime() - 24 * 60 * 60 * 1000);
		const lookbackFrom = new Date(lookbackTo.getTime() - option.days * 24 * 60 * 60 * 1000);
		return {
			from: iso(from),
			to: iso(to),
			lookbackFrom: iso(lookbackFrom),
			lookbackTo: iso(lookbackTo),
			days: option.days,
			label: option.label,
			value: option.value
		};
	}, [option.days, option.value, option.label]);

	const setRange = (next) => {
		const params = new URLSearchParams(searchParams);
		if (next === DEFAULT_RANGE) params.delete('range');
		else params.set('range', next);
		setSearchParams(params, { replace: true });
	};

	return { ...range, setRange };
}
